import SelectController, { SelectControllerProps } from "@/common/components/SelectController";
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import Modal from "@/common/components/Modal";
import { Dispatch, useEffect, useState } from "react";
import { useProducts } from "@/common/hooks/queries/useProducts";
import { ProductData } from "@/common/types/product.type";
import { Mapper } from "@/common/services/mapper";
import { useCreateEstablishmentProduct } from "../../hooks/useCreateEstablishmentProduct";
import { capitalizeFirstLetter } from "@/common/utils/capitalizeFirstLetter";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { createEstablishmentProductSchema } from "../../schemas/createProduct.schema";
import { EstablishmentProductPricesForm } from "../../types/establishmentProductPricesForm.types";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { EstablishmentProductPrice } from "../../types/establishmentProducts.type";

type Props = {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<boolean>;
  branchId?: string;
  companyProductCategories?: EstablishmentCompanyData['company_product_categories']
  productsPrices?: EstablishmentProductPrice[]
};

const ModalToAddProduct = ({
  isModalVisible,
  setIsModalVisible,
  branchId = "",
  companyProductCategories,
  productsPrices
}: Props) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
    setError,
    reset
  } = useForm<EstablishmentProductPricesForm>({resolver: zodResolver(createEstablishmentProductSchema), defaultValues: {
    product_id: {
      label: "",
      value: "",
    },
    metric: "",
  },});
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState<SelectControllerProps<any>['options']>([]);
  const queryClient = useQueryClient();
  const { mutate: createProduct } = useCreateEstablishmentProduct({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentProducts'] })
      setIsModalVisible(false);
    },
    onError: FormTransformers.errorsTransformer<EstablishmentProductPricesForm>({ setError }),
  });

  const fuelCategoryId = companyProductCategories?.find((item) => item.product_category.perma_name === 'fuel')?.product_category.id

  const { data: products } = useProducts<ProductData[]>({
    filters: {
      search: productSearch,
      category: fuelCategoryId
    },
    enabled: !!fuelCategoryId
  });

  useEffect(() => {
    reset()
  }, [isModalVisible])

  useEffect(() => {
    if(products) {
      const mapper = Mapper.mapToOptions<ProductData>({
        labelFieldName: "display_name",
        valueFieldName: "id",
      })

      const existingProductIds = new Set(productsPrices?.map((prodPrice) => prodPrice?.product?.id))
      const availableProducts = products.filter((product) => !existingProductIds.has(product?.id))

      const filteredProducts = mapper(availableProducts)
      setProductOptions(filteredProducts)
    }
  }, [products[0]?.id, productsPrices?.[0]?.id])

  const onSubmit: SubmitHandler<EstablishmentProductPricesForm> = (data) => {
    (data as any)['branch_id'] = branchId;
    createProduct(data);
  };

  return (
    <Modal
      isOpen={isModalVisible}
      setIsOpen={setIsModalVisible}
      title="Adicionar Novo Produto"
      footer="Confirmar adição e salvar alteração"
      dataTestid="submit_button"
      onConfirm={handleSubmit(onSubmit)}
      body={
        <form className="flex flex-col gap-4">
          <SelectController
            control={control}
            id="product_id"
            name="product_id"
            className="text-white-800"
            label="Produto"
            onInputChange={(e) => setProductSearch(e)}
            options={productOptions}
            errorMessage={capitalizeFirstLetter(errors.product_id?.value?.message || errors.product_id?.message )}
            markAsRequired
          />
          <Controller
            name="metric"
            control={control}
            render={({ field }) => (
              <CurrencyTextField
                label="Valor do produto"
                markAsRequired
                className="text-white-800"
                errorMessage={errors.metric?.message}
                {...field}
              />
            )}
          />
        </form>
      }
    />
  );
};

export default ModalToAddProduct;
